/**
 * Suspicious v2 §3.4 — the "Turn on blocking" inline link + its
 * ConfirmDialog + PUT /v1/sites/:site_id/firewall-mode wiring.
 *
 * Shared between SenderDetail and PersonTimeline so one implementation
 * drives both status lines. The link only renders for owner / admin
 * (reviewer / viewer / support / acting-agency pass `canMutate: false`
 * and the status line stays unlinked). A 409 `install_cannot_enforce`
 * swaps the status line to the "Update your Cloudflare Worker" copy
 * via the `onModeFlipped` callback (install override).
 */

import { useCallback, useState } from 'react';

import type { BlockingState } from '@sill/types/api';

import type { HttpClient } from '../../api/http-client.js';
import { putFirewallMode } from '../../api/threats.js';
import { ConfirmDialog } from '../../components/atoms/index.js';
import { useOptionalSites } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import { INSTALL_HREF } from '../security/server-install.js';
import { BlockStatusLine } from './BlockControl.js';

const sansFont = "'Geist', sans-serif";

export interface TurnOnBlockingLinkProps {
  readonly siteId: string;
  readonly siteDomain: string | null;
  readonly http: HttpClient | null;
  readonly blocking: BlockingState;
  /**
   * Blocks already saved on the site, surfaced in the confirm body so
   * the merchant can see how many addresses this will suddenly start
   * stopping. 0 is a valid value — the confirm renders "0 addresses"
   * then and still asks the question.
   */
  readonly liveBlocksCount: number;
  /** When falsey the status line renders without the inline link. */
  readonly canMutate?: boolean | undefined;
  /**
   * On a successful flip, called with `null` so the caller updates
   * the local blocking.mode to 'on'. On a 409 install_cannot_enforce,
   * called with 'worker_outdated' | 'not_in_path' so the status line
   * can swap to the "Update your Cloudflare Worker" / "Set up the
   * Worker" copy without a full refetch.
   */
  readonly onModeFlipped: (install_override: 'worker_outdated' | 'not_in_path' | null) => void;
}

export function TurnOnBlockingLink(props: TurnOnBlockingLinkProps): JSX.Element {
  const C = useC();
  const canMutate = props.canMutate !== false;
  // Founder 2026-10-29: the turn-on confirm only makes sense with a
  // current Worker in front of the site. "Set up the Worker" and
  // "Update the Worker" link to Settings › Install instead.
  const canFlipFromHere = canMutate && props.blocking.mode === 'off' && props.blocking.install === 'worker_ready';
  const sites = useOptionalSites();
  const site = sites?.sites.find((x) => x.site_id === props.siteId) ?? sites?.activeSite ?? null;
  const shopify = site?.detected_platform === 'shopify';
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const flip = useCallback(async () => {
    if (props.http === null) {
      setError('The dashboard is offline.');
      setConfirming(false);
      return;
    }
    setPending(true);
    setError(null);
    try {
      const res = await putFirewallMode(props.http, props.siteId, 'enforce');
      if (res.ok) {
        setConfirming(false);
        props.onModeFlipped(null);
      } else if (res.error === 'install_cannot_enforce') {
        setConfirming(false);
        // Tell the caller to switch the status line to the "update /
        // set up the Worker" copy. Server returns additional
        // install_source + install_version context on the 409 body;
        // we don't need to parse them to pick a label — any
        // install-not-Worker lands as 'not_in_path', and a Worker
        // below 1.8 lands as 'worker_outdated'. We default to
        // 'worker_outdated' here as the common case; the real
        // discriminator is only visible server-side, so the UI copy
        // adapts to whatever the Worker install actually reports on
        // the next refetch.
        const override: 'worker_outdated' | 'not_in_path' =
          props.blocking.install === 'not_in_path' ? 'not_in_path' : 'worker_outdated';
        props.onModeFlipped(override);
      } else if (res.error === 'forbidden') {
        setError("You don't have permission to change blocking on this site.");
      } else if (res.error === 'plan_feature_required') {
        // ADR-0263 S4 — site's resolved v2 plan does not include
        // `blocking`. The merchant sees the plan name when the server
        // has one to offer; otherwise a plain unavailable message.
        setError(res.plan_name !== null && res.plan_name !== undefined
          ? `Blocking is part of the ${res.plan_name} plan.`
          : "Blocking is not on this site's plan.");
      } else if (res.error === 'rate_limited') {
        setError('Too many changes just now — try again in a minute.');
      } else {
        setError("That didn't work. Try again.");
      }
    } finally {
      setPending(false);
    }
  }, [props]);

  const linkCallback = canFlipFromHere ? () => setConfirming(true) : undefined;

  return (
    <>
      <BlockStatusLine
        blocking={props.blocking}
        siteDomain={props.siteDomain}
        liveBlocksCount={props.liveBlocksCount}
        {...(linkCallback !== undefined ? { onTurnOn: linkCallback } : {})}
        {...(canMutate ? { setupHref: INSTALL_HREF } : {})}
        shopify={shopify}
      />
      {error !== null ? (
        <div
          data-testid="turn-on-blocking-error"
          style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg, marginTop: 6 }}
        >
          {error}
        </div>
      ) : null}
      <ConfirmDialog
        open={confirming && !pending}
        onClose={() => { if (!pending) setConfirming(false); }}
        title={`Turn on blocking for ${props.siteDomain ?? 'this site'}?`}
        body={bodyText(props.liveBlocksCount, props.siteDomain)}
        confirmLabel="Turn on blocking"
        confirmTone="lime"
        onConfirm={() => { void flip(); }}
      />
    </>
  );
}

function bodyText(n: number, domain: string | null): string {
  const addrs = n === 1 ? '1 address you’ve blocked' : `${n.toLocaleString('en-US')} addresses you've blocked`;
  const d = domain ?? 'this site';
  // Founder bug 2026-10-18: name the site in the Turn-on confirm so
  // the merchant sees exactly which domain blocking starts enforcing on.
  return `Blocking will start enforcing on ${d} within a minute. Requests from the ${addrs} will get “Access denied.” Verified search and AI crawlers are never blocked. You can turn this off any time.`;
}
